import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api.js';
import { DestinationFeature } from '@mpr/shared';
import { DestinationFilter } from './DestinationPicker.js';
import { pageDestinationId, pageIdOf } from '../lib/destinations.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { ErrorNotice } from './ErrorNotice.js';
import { ImageCropper } from './ImageCropper.js';
import { Icon } from './Icons.js';

/**
 * Editing a Page's own profile.
 *
 * Five fields, all of them plain text a Page owner recognises from Facebook's
 * own settings. Everything structured — opening hours, a location, a
 * call-to-action button — has its own shape and its own ways of failing, and
 * belongs in its own screen rather than smuggled into a text box.
 *
 * The Page's name is shown but not editable: Meta puts name changes through a
 * review of its own, and a field that silently fails is worse than one that was
 * never offered.
 */

/* Mirrors EDITABLE_FIELDS on the server, which is what the route allow-lists. */
type Field = 'about' | 'description' | 'phone' | 'emails' | 'website';

export function PageProfilePanel() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [pageId, setPageId] = useState('');
  const [draft, setDraft] = useState<Record<Field, string>>({
    about: '',
    description: '',
    phone: '',
    emails: '',
    website: '',
  });

  const pages = useQuery({ queryKey: ['pages'], queryFn: api.pages });
  const profile = useQuery({
    queryKey: ['page-profile', pageId],
    queryFn: () => api.pageProfile(pageId),
    enabled: pageId !== '',
  });

  /*
   * The form is filled from Meta whenever a different Page is chosen, and left
   * alone afterwards — otherwise a background refetch would overwrite half-typed
   * edits, which is how the sign-in form used to lose its contents.
   */
  useEffect(() => {
    const loaded = profile.data?.profile;
    if (!loaded) return;
    setDraft({
      about: loaded.about ?? '',
      description: loaded.description ?? '',
      phone: loaded.phone ?? '',
      emails: loaded.email ?? '',
      website: loaded.website ?? '',
    });
    setHours(loaded.hours);
    setPlace({
      street: loaded.location.street ?? '',
      city: loaded.location.city ?? '',
      state: loaded.location.state ?? '',
      country: loaded.location.country ?? '',
      zip: loaded.location.zip ?? '',
    });
  }, [profile.data?.profile.id]);

  /*
   * Hours are edited as a whole week and sent as a whole week: Meta clears a
   * day only when it is sent empty, so a partial update could never remove one.
   */
  const [hours, setHours] = useState<
    Array<{ day: string; open: string | null; close: string | null }>
  >([]);

  const setDay = (day: string, part: 'open' | 'close' | 'clear', value: string) =>
    setHours((current) =>
      current.map((entry) =>
        entry.day !== day
          ? entry
          : part === 'clear'
            ? { ...entry, open: null, close: null }
            : { ...entry, [part]: value || null },
      ),
    );

  const [place, setPlace] = useState({
    street: '',
    city: '',
    state: '',
    country: '',
    zip: '',
  });

  const saveLocation = useMutation({
    mutationFn: () => api.updatePageLocation(pageId, place),
    onSuccess: (data) => queryClient.setQueryData(['page-profile', pageId], data),
  });

  /*
   * A picked file is not uploaded straight away: it opens the cropper, and only
   * what comes back from there is sent. Facebook crops to its own shape either
   * way — this just makes the choice yours instead of its.
   */
  const [pending, setPending] = useState<{
    kind: 'picture' | 'cover';
    file: File;
  } | null>(null);

  const image = useMutation({
    mutationFn: ({ kind, file }: { kind: 'picture' | 'cover'; file: File }) =>
      api.updatePageImage(pageId, kind, file),
    onSuccess: (data) => queryClient.setQueryData(['page-profile', pageId], data),
  });

  const saveHours = useMutation({
    mutationFn: () => api.updatePageHours(pageId, hours),
    onSuccess: (data) => queryClient.setQueryData(['page-profile', pageId], data),
  });

  const save = useMutation({
    mutationFn: () => api.updatePageProfile(pageId, draft),
    onSuccess: (data) => {
      queryClient.setQueryData(['page-profile', pageId], data);
      void queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  const editable = pages.data?.pages.filter((page) => page.isActive && page.canPublish) ?? [];
  const current = profile.data?.profile;

  return (
    <section className="panel">
      <h2>{t('profile.title')}</h2>
      <p className="muted small">{t('profile.subtitle')}</p>

      <label htmlFor="profile-page">{t('profile.selectPage')}</label>
      <DestinationFilter
        id="profile-page"
        feature={DestinationFeature.PROFILE}
        required
        value={pageId ? pageDestinationId(pageId) : ''}
        allLabel={t('profile.selectPagePlaceholder')}
        onChange={(id) => setPageId(pageIdOf(id))}
      />

      {editable.length === 0 && pages.data && (
        <div className="notice warn">{t('profile.noEditablePages')}</div>
      )}

      {profile.isError && <ErrorNotice error={profile.error} />}
      {profile.isLoading && pageId && <div className="loading">{t('common.loading')}</div>}

      {current && (
        <>
          <div className="card profile-head">
            {current.pictureUrl && (
              <img src={current.pictureUrl} alt="" className="profile-avatar" />
            )}
            <div className="profile-head-body">
              <strong>{current.name}</strong>
              <span className="muted small">
                {current.category}
                {current.followers !== null &&
                  ` · ${t('profile.followers', { count: current.followers })}`}
              </span>
            </div>
            {current.link && (
              <a href={current.link} target="_blank" rel="noreferrer" className="ghost small">
                {t('profile.openOnFacebook')}
              </a>
            )}
          </div>

          {/* Shown, never editable: Meta reviews Page name changes itself. */}
          <p className="muted small">{t('profile.nameNote')}</p>

          <label htmlFor="p-about">{t('profile.about')}</label>
          <input
            id="p-about"
            value={draft.about}
            maxLength={255}
            onChange={(event) => setDraft({ ...draft, about: event.target.value })}
          />

          <label htmlFor="p-description">{t('profile.description')}</label>
          <textarea
            id="p-description"
            rows={4}
            value={draft.description}
            onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          />

          <label htmlFor="p-phone">{t('profile.phone')}</label>
          <input
            id="p-phone"
            className="ltr"
            value={draft.phone}
            onChange={(event) => setDraft({ ...draft, phone: event.target.value })}
          />

          <label htmlFor="p-email">{t('profile.email')}</label>
          <input
            id="p-email"
            type="email"
            className="ltr"
            value={draft.emails}
            onChange={(event) => setDraft({ ...draft, emails: event.target.value })}
          />

          <label htmlFor="p-website">{t('profile.website')}</label>
          <input
            id="p-website"
            className="ltr"
            placeholder="https://"
            value={draft.website}
            onChange={(event) => setDraft({ ...draft, website: event.target.value })}
          />

          {save.isError && (
            <div className="notice error">
              {save.error instanceof ApiError ? save.error.message : t('profile.saveFailed')}
            </div>
          )}
          {save.isSuccess && <div className="notice ok">{t('profile.saved')}</div>}

          <button
            type="button"
            className="primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            <Icon.check />
            {save.isPending ? t('profile.saving') : t('profile.save')}
          </button>

          {/* Meta normalises some values and refuses others. What comes back is
              what is actually stored, so it is worth saying that plainly. */}
          <p className="muted small">{t('profile.readBackNote')}</p>

          <h2 style={{ marginTop: 30 }}>{t('profile.images')}</h2>
          <div className="image-row">
            <ImageSlot
              label={t('profile.picture')}
              url={current.pictureUrl}
              shape="square"
              busy={image.isPending && image.variables?.kind === 'picture'}
              onPick={(file) => setPending({ kind: 'picture', file })}
            />
            <ImageSlot
              label={t('profile.cover')}
              url={current.coverUrl}
              shape="wide"
              busy={image.isPending && image.variables?.kind === 'cover'}
              onPick={(file) => setPending({ kind: 'cover', file })}
            />
          </div>
          {pending && (
            <ImageCropper
              file={pending.file}
              kind={pending.kind}
              onCancel={() => setPending(null)}
              onDone={(cropped) => {
                image.mutate({ kind: pending.kind, file: cropped });
                setPending(null);
              }}
            />
          )}

          {image.isError && <ErrorNotice error={image.error} />}

          <h2 style={{ marginTop: 30 }}>{t('profile.location')}</h2>
          <p className="muted small">{t('profile.locationHint')}</p>

          <div className="place-grid">
            {(['street', 'city', 'state', 'zip', 'country'] as const).map((field) => (
              <div key={field}>
                <label htmlFor={`pl-${field}`}>{t(`profile.place.${field}` as never)}</label>
                <input
                  id={`pl-${field}`}
                  value={place[field]}
                  onChange={(event) => setPlace({ ...place, [field]: event.target.value })}
                />
              </div>
            ))}
          </div>

          {saveLocation.isError && <ErrorNotice error={saveLocation.error} />}
          {saveLocation.isSuccess && <div className="notice ok">{t('profile.locationSaved')}</div>}

          <button
            type="button"
            className="primary"
            onClick={() => saveLocation.mutate()}
            disabled={saveLocation.isPending}
          >
            <Icon.check />
            {saveLocation.isPending ? t('profile.saving') : t('profile.saveLocation')}
          </button>

          <h2 style={{ marginTop: 30 }}>{t('profile.hours')}</h2>
          <p className="muted small">{t('profile.hoursHint')}</p>

          <div className="hours-grid">
            {hours.map((entry) => (
              <div className="hours-row" key={entry.day}>
                <span className="hours-day">{t(`profile.day.${entry.day}` as never)}</span>
                <input
                  type="time"
                  value={entry.open ?? ''}
                  aria-label={`${entry.day} open`}
                  onChange={(event) => setDay(entry.day, 'open', event.target.value)}
                />
                <span className="muted">—</span>
                <input
                  type="time"
                  value={entry.close ?? ''}
                  aria-label={`${entry.day} close`}
                  onChange={(event) => setDay(entry.day, 'close', event.target.value)}
                />
                {(entry.open || entry.close) && (
                  <button
                    type="button"
                    className="ghost small"
                    onClick={() => setDay(entry.day, 'clear', '')}
                  >
                    {t('profile.closed')}
                  </button>
                )}
              </div>
            ))}
          </div>

          {saveHours.isError && <ErrorNotice error={saveHours.error} />}
          {saveHours.isSuccess && <div className="notice ok">{t('profile.hoursSaved')}</div>}

          <button
            type="button"
            className="primary"
            onClick={() => saveHours.mutate()}
            disabled={saveHours.isPending}
          >
            <Icon.check />
            {saveHours.isPending ? t('profile.saving') : t('profile.saveHours')}
          </button>
        </>
      )}
    </section>
  );
}

function ImageSlot({
  label,
  url,
  shape,
  busy,
  onPick,
}: {
  label: string;
  url: string | null;
  shape: 'square' | 'wide';
  busy: boolean;
  onPick: (file: File) => void;
}) {
  const { t } = useI18n();
  const inputId = `img-${shape}`;

  return (
    <div className={`image-slot ${shape}`}>
      <span className="image-label">{label}</span>
      <div className="image-frame">
        {url ? (
          <img src={url} alt="" />
        ) : (
          <span className="muted small">{t('profile.noImage')}</span>
        )}
        {busy && (
          <div className="image-busy">
            <Icon.refresh className="spin" />
          </div>
        )}
      </div>
      {/* A styled label rather than a styled file input: the native control
          cannot be made to match anything else on the screen. */}
      <label htmlFor={inputId} className="ghost small image-pick">
        {t('profile.replace')}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onPick(file);
          // Cleared so choosing the same file twice still fires a change.
          event.target.value = '';
        }}
      />
    </div>
  );
}
